import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toValidObjectIdString, formatApiDate } from '../utils/security';
import { fetchCihazDegisimleri, fetchCihazDegisimiDetay } from '../services/cihazDegisimiService';
import CihazDegisimiDetayModal from '../components/CihazDegisimiDetayModal';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const TABS = [
  { id: 'onay-bekleyen', label: 'Onay Bekleyen', tab: 'onay-bekleyen' },
  { id: 'onaylanan', label: 'Onaylanan', tab: 'onaylanan' },
];

const getCihazDegisimiId = (c) => toValidObjectIdString(c?.id ?? c?._id) || '';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const TAB_IDS = TABS.map((t) => t.id);

const CihazDegisimiAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const tabFromUrl = searchParams.get('tab');
  const initialTab = TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'onay-bekleyen';

  const [cihazDegisimleri, setCihazDegisimleri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedDetay, setSelectedDetay] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const loadCihazDegisimleri = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tab = activeTab === 'onaylanan' ? 'onaylanan' : 'onay-bekleyen';
      const response = await fetchCihazDegisimleri({ tab, sayfa: currentPage, limit: PAGE_DEFAULT_LIMIT });
      if (response?.basarili && response?.veri) {
        setCihazDegisimleri(response.veri.cihazDegisimleri || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Cihaz değişimleri yüklenemedi'));
      setCihazDegisimleri([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    if (!tabFromUrl || !TAB_IDS.includes(tabFromUrl)) {
      setSearchParams({ tab: initialTab }, { replace: true });
    }
  }, [tabFromUrl, initialTab, setSearchParams]);

  useEffect(() => {
    loadCihazDegisimleri();
  }, [loadCihazDegisimleri]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    }, { replace: true });
  };

  const handleViewDetail = async (item) => {
    const id = getCihazDegisimiId(item);
    if (!id) {
      window.showNotification?.('error', 'Cihaz değişimi ID alınamadı');
      return;
    }
    try {
      const response = await fetchCihazDegisimiDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedDetay(response.veri);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  return (
    <main className="main-content cihaz-degisimi-admin">
      <div className="content-header">
        <h1>Cihaz Değişimi Yönetimi</h1>
      </div>

      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Cihaz değişimleri yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : cihazDegisimleri.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-exchange-alt" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Cihaz değişimi talebi bulunamadı</div>
          </div>
        ) : (
          <>
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Cihaz Değişim No</th>
                  <th>Randevu No</th>
                  <th>Cihaz (Seri No / Ürün)</th>
                  <th>Teknik Servis</th>
                  <th>Tarih</th>
                  <th>Durum</th>
                  <th className="islemler-sutunu">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {cihazDegisimleri.map((c, idx) => (
                  <tr key={getCihazDegisimiId(c) || idx}>
                    <td style={{ fontWeight: 500 }}>{c.cihazDegisimiNumarasi}</td>
                    <td>{c.randevuNumarasi || '—'}</td>
                    <td>{c.seriNumarasi ? `${c.seriNumarasi}${c.urunAdi ? ` / ${c.urunAdi}` : ''}` : c.urunAdi || '—'}</td>
                    <td>{c.teknikServisAdi || '—'}</td>
                    <td>{formatApiDate(c.olusturmaTarihi)}</td>
                    <td>
                      <span className={`status-badge ${
                        c.durum === 'OnayBekliyor' ? 'status-bekliyor' :
                        c.durum === 'Onaylandi' ? 'status-tamamlandi' :
                        c.durum === 'IptalEdildi' ? 'status-iptal' : ''
                      }`}>
                        {c.durum === 'OnayBekliyor' ? 'Onay Bekliyor' :
                         c.durum === 'Onaylandi' ? 'Onaylandı' :
                         c.durum === 'IptalEdildi' ? 'İptal Edildi' : c.durum}
                      </span>
                    </td>
                    <td className="table-actions islemler-sutunu">
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={() => handleViewDetail(c)}
                        title="Detay"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={currentPage}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setCurrentPage}
            loading={loading}
            embedded
          />
          </>
        )}
      </div>

      {detailModalOpen && selectedDetay && (
        <CihazDegisimiDetayModal
          cihazDegisimi={selectedDetay}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedDetay(null);
          }}
          onRefresh={loadCihazDegisimleri}
          isAdmin={true}
        />
      )}
    </main>
  );
};

export default CihazDegisimiAdminPage;
